/**
 * Offline punch dispatch logic (API-4650).
 *
 * This is the decision layer that intercepts a punch attempt in
 * `PunchFlowContext` *before* KMP's `PunchFlowService.start()` is called.
 * When the terminal is offline, the punch never reaches KMP — everything
 * here stays within the React layer, reading the LDR cache (API-4378) and
 * writing straight to the offline punch queue (API-4378).
 *
 * Two entry points, matching the two places a punch can finish:
 *   - `handleOfflinePunch`   — the initial decision: lockout check, then
 *     either resolve immediately (no role/department needed) or hand back
 *     to the caller for role/department selection (API-4645).
 *   - `completeOfflinePunch` — resumes after a role or department has been
 *     picked, going straight to building + storing the record.
 *
 * Only one of role/department selection is ever needed for a given LDR
 * cache: roles take priority over departments (see `handleOfflinePunch`).
 */

import type { ClockRole, ClockDepartment } from '@src/api/punchTypes';
import { getLDR } from '@src/config/ldrCache';
import { addPunch } from '@src/config/offlinePunchQueue';
import { isOfflineLimitExceeded } from '@src/config/offlineLimit';

export type OfflinePunchResult =
    | { type: 'stored'; punchTime: Date }
    | { type: 'needsRoleSelection'; roles: ClockRole[] }
    | { type: 'needsDepartmentSelection'; departments: ClockDepartment[] }
    | { type: 'limitExceeded' }
    | { type: 'error'; reason: string };

export interface OfflinePunchSessionContext {
    companyId: number;
    locationId: number;
    locationHash: string;
    offlineSince: Date | null;
}

export interface OfflinePunchSelection {
    roleId?: number;
    departmentId?: number;
}

async function buildAndStorePunch(
    pin: string,
    companyId: number,
    locationHash: string,
    selection: OfflinePunchSelection
): Promise<OfflinePunchResult> {
    const punchTime = new Date();
    try {
        await addPunch({
            company_id: companyId,
            location_hash: locationHash,
            employee_id: pin,
            time: punchTime.toISOString(),
            role_id: selection.roleId ?? null,
            department_id: selection.departmentId ?? null,
            tips: null
        });
        return { type: 'stored', punchTime };
    } catch (err) {
        console.error('[offlinePunchService] Failed to store offline punch', err);
        const reason = err instanceof Error ? err.message : 'unknown_error';
        return { type: 'error', reason };
    }
}

/**
 * Entry point for a fresh offline punch attempt (PIN just entered).
 *
 * a. Checks the offline time limit (lockout, API-4643).
 * b. Reads the LDR cache for the current company/location.
 * c/d. If roles or departments are cached, hands back to the caller so a
 *    selection modal (API-4645) can be shown — `completeOfflinePunch` picks
 *    up from there once a choice is made.
 * e/f/g. Otherwise builds and stores the punch immediately.
 */
export async function handleOfflinePunch(
    pin: string,
    session: OfflinePunchSessionContext
): Promise<OfflinePunchResult> {
    const { companyId, locationId, locationHash, offlineSince } = session;

    if (isOfflineLimitExceeded(offlineSince)) {
        return { type: 'limitExceeded' };
    }

    const ldr = await getLDR(companyId, locationId);

    if (ldr && ldr.roles.length > 0) {
        return { type: 'needsRoleSelection', roles: ldr.roles };
    }

    if (ldr && ldr.departments.length > 0) {
        return { type: 'needsDepartmentSelection', departments: ldr.departments };
    }

    return buildAndStorePunch(pin, companyId, locationHash, {});
}

/**
 * Resumes an offline punch after a role or department has been selected
 * (API-4645). Builds and stores the record with the chosen selection.
 */
export async function completeOfflinePunch(
    pin: string,
    session: Pick<OfflinePunchSessionContext, 'companyId' | 'locationHash'>,
    selection: OfflinePunchSelection
): Promise<OfflinePunchResult> {
    return buildAndStorePunch(pin, session.companyId, session.locationHash, selection);
}
